import React, { useState, useEffect } from "react";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
const staticLinks = [{
  label: "Features",
  href: "/SilentOperatorPage"
}, {
  label: "Pricing",
  href: "/PricingPage"
}, {
  label: "About",
  href: "/AboutPage"
}, {
  label: "Support",
  href: "https://thinklocalpro.com/support-ticket"
}];
function HeaderMenuConsumer({
  isMobile = false,
  onItemClick
}: {
  isMobile?: boolean;
  onItemClick?: () => void;
}) {
  return <nav className={cn("flex", isMobile ? "flex-col gap-6 items-center" : "gap-8 items-center")}>
      {staticLinks.map((item, i) => <Link key={item.label} to={item.href} className={cn("font-serif font-bold uppercase tracking-widest transition-colors", isMobile ? "text-xl text-white/90 hover:text-primary" : "text-xs text-white/80 hover:text-primary")} onClick={() => {
      if (onItemClick) onItemClick();
    }} data-index={i}>
          {item.label}
        </Link>)}
    </nav>;
}
export default function NavigationHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        const label = anchor.textContent?.trim();
        if (label === "Support") return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b dark bg-[#1A1A1A]/80 backdrop-blur-md text-white", isScrolled ? "border-white/10 shadow-xl shadow-black/40" : "border-transparent")}>
      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12 h-[90px] flex items-center justify-between">
        <div className="flex-shrink-0 z-50 py-2">
          <Link to="/HomePage" className="block">
            <img src="https://singular-penguin-1-dev.thinklocalweb.com/wp-content/uploads/2026/05/Website-Logo-scaled.png" data-wvc-srcset="/img/thinklocalpro_rectangular_logo.png-wvc-srcset" data-wvc-sizes="/img/thinklocalpro_rectangular_logo.png-wvc-sizes" alt="ThinkLocalPro.com" className="h-[80px] w-auto object-contain" loading="lazy" />
          </Link>
        </div>

        <div className="hidden md:flex items-center justify-center flex-1 px-8">
          <HeaderMenuConsumer />
        </div>

        <div className="flex items-center gap-4 z-50">
          <Link to="https://app.thinklocalpro.com" className="text-xs font-serif font-bold uppercase tracking-widest text-white/80 hover:text-primary transition-colors">
            Login
          </Link>

          <Button asChild className="flex bg-linear-90 from-[#C79100] to-[#E6B800] text-black hover:opacity-90 rounded-md h-10 px-3 md:px-6 transition-all duration-300 group font-serif font-bold uppercase tracking-wider border-none">
            <Link to="/PricingPage">
              Secure Founder Spot
              <ArrowUpRight className="ml-2 size-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
            </Link>
          </Button>

          <Button variant="ghost" size="icon" className="md:hidden text-primary hover:bg-white/5 hover:text-primary" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      <div className={cn("fixed inset-0 z-40 bg-[#1A1A1A]/95 backdrop-blur-xl transition-all duration-300 md:hidden flex flex-col items-center justify-center", isMobileMenuOpen ? "opacity-100 visible" : "opacity-0 invisible pointer-events-none")} style={{
      top: "90px",
      height: "calc(100vh - 90px)"
    }}>
        <div className="w-full max-w-md px-6 py-8 flex flex-col gap-8 items-center text-center">
          <HeaderMenuConsumer isMobile onItemClick={() => setIsMobileMenuOpen(false)} />
          
          <div className="w-full h-px bg-white/10 my-2" />
          
          <Button asChild className="w-full bg-linear-90 from-[#C79100] to-[#E6B800] text-black hover:opacity-90 rounded-md h-12 text-lg font-serif font-bold uppercase tracking-wider border-none">
            <Link to="/PricingPage" onClick={() => setIsMobileMenuOpen(false)}>
              Secure Founder Spot
              <ArrowUpRight className="ml-2 size-5" />
            </Link>
          </Button>
        </div>
      </div>
    </header>;
}